import { RewardProgramFragment } from '@graphql/fragments/__generated__/RewardProgram.generated'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { ProductPriceRangeType, OutOfStockStatusEnum } from '../../../generated/gqlTypes'
import { RewardProgramButton } from '../RewardProgramButton'
import { AfterPayInfo } from '@components/AfterPayInfo'
import { PromoCodeFragment } from '@graphql/fragments/__generated__/PromoCode.generated'
import { useQueryParams } from 'use-query-params'
import { PROMO_CODES_DIALOG_PARAMS } from '@components/Header/components/PromoCodes'
import { Trans, useTranslation } from 'react-i18next'
import FormattedPrice from '@components/FormattedPrice'

const IN_STOCK_SCHEMA = 'https://schema.org/InStock'
const OUT_STOCK_SCHEMA = 'https://schema.org/OutOfStock'

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
  },
  priceLine: {
    fontWeight: 700,
    lineHeight: '25px',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  priceValue: {
    fontSize: 16,
    paddingRight: '7px',
  },
  priceValueSmall: {
    paddingRight: '7px',
    fontSize: 13,
  },
  rewards: {
    fontSize: 12,
    fontWeight: 400,
  },
}))

interface IProps {
  selectedVariant: {
    priceRange: ProductPriceRangeType
    stockQuantity: number
    shippingRequired: boolean
    outOfStockStatus: string
    isSupply: boolean
    stockStatus: OutOfStockStatusEnum | null
  } | null
  quantity?: number
  showStatus?: boolean
  product?: {
    priceRange?: ProductPriceRangeType
    rewardPrograms?: RewardProgramFragment[]
  }
  promoCodes?: PromoCodeFragment[]

  checkVariantAvailability?(): boolean
}

const ProductVariantPrice: React.FC<IProps> = ({
  selectedVariant,
  checkVariantAvailability,
  quantity = 1,
  showStatus = true,
  product,
  promoCodes,
}) => {
  const { t } = useTranslation()
  const [, setParams] = useQueryParams(PROMO_CODES_DIALOG_PARAMS)
  const classes = useStyles()

  const priceRange = selectedVariant?.priceRange || product?.priceRange

  if (!priceRange) return null

  let variantSchemaAvailability, stock
  const priceCurrency = priceRange.currency
  const isPriceRange = priceRange.discount.min !== priceRange.discount.max
  if (selectedVariant) {
    variantSchemaAvailability =
      selectedVariant.stockQuantity > 0 ||
      !selectedVariant.shippingRequired ||
      ['IN', 'BACKORDER'].includes(selectedVariant.outOfStockStatus)
        ? IN_STOCK_SCHEMA
        : OUT_STOCK_SCHEMA
    if (checkVariantAvailability && showStatus) {
      if (!selectedVariant) {
        stock = <span />
      } else if (selectedVariant.stockStatus === OutOfStockStatusEnum.Backorder) {
        stock = <span className='stock-status dynamic yellow'>{t('productBackorder', 'Backorder')}</span>
      } else if (selectedVariant.stockStatus === OutOfStockStatusEnum.In) {
        stock = <span className='stock-status dynamic green'>{t('productInStock', 'In Stock')}</span>
      } else if (selectedVariant.stockStatus === OutOfStockStatusEnum.Out) {
        stock = <span className='stock-status dynamic red'>{t('productOutOfStock', 'Out of Stock')}</span>
      } else {
        stock = <span />
      }
    }
  }

  const sale = priceRange.discount.max < priceRange.undiscount.max
  let salePercent, saleText, saleStatus
  if (sale) {
    salePercent = 100 - (priceRange.discount.max * 100) / priceRange.undiscount.max
    saleText = (
      <span className='sale'>
        <Trans
          i18nKey='wasPrice'
          defaults={`Was <span><price/></span>`}
          components={{
            span: <span className='line-through' />,
            price: <FormattedPrice currency={priceCurrency} amount={priceRange.undiscount.min * quantity} />,
          }}
        />
      </span>
    )
    saleStatus = (
      <span className='stock-status dynamic orange'>
        {t('savePrice', { defaultValue: 'Save {{salePercent}}%', salePercent: Math.round(salePercent) })}
      </span>
    )
  }

  const rewardProgram = product?.rewardPrograms && product.rewardPrograms[0]
  let totalRewardPointsMin = 0
  let totalRewardPointsMax = 0
  if (rewardProgram) {
    totalRewardPointsMin = priceRange.discount.min * rewardProgram.coefficient * quantity
    totalRewardPointsMax = priceRange.discount.max * rewardProgram.coefficient * quantity
  }
  return (
    <div className={classes.wrapper}>
      {sale && !selectedVariant?.isSupply ? saleText : ''}
      <ul className='list-unstyled'>
        <li className={classes.priceLine} itemProp='offers'>
          <div>
            <span className='flex items-start'>
              <meta itemProp='priceCurrency' content={priceCurrency} />
              <link itemProp='availability' href={variantSchemaAvailability} />
              <span itemProp='price' className={product ? classes.priceValue : classes.priceValueSmall}>
                {isPriceRange ? (
                  <>
                    <FormattedPrice currency={priceCurrency} amount={priceRange.discount.min * quantity} /> -{' '}
                    <FormattedPrice currency={priceCurrency} amount={priceRange.discount.max * quantity} />
                  </>
                ) : (
                  <FormattedPrice currency={priceCurrency} amount={priceRange.discount.min * quantity} />
                )}
              </span>

              {sale ? saleStatus : ''}
              {showStatus && (stock || '')}
            </span>
            {!sale && !!totalRewardPointsMin && (
              <div className={`text-gray-800 ${classes.rewards}`}>
                {totalRewardPointsMin === totalRewardPointsMax
                  ? t('productEarnPointsMin', { defaultValue: 'Earn {{count}} points', count: totalRewardPointsMin })
                  : t('productEarnPointsMax', {
                      defaultValue: 'Earn up to {{count}} points',
                      count: totalRewardPointsMax,
                    })}
              </div>
            )}
          </div>
          {!sale && <RewardProgramButton rewardProgram={rewardProgram} />}
        </li>
      </ul>
      <div className='mb-10 text-13'>
        <AfterPayInfo minPrice={priceRange.discount.min} maxPrice={priceRange.discount.max} currency={priceCurrency} />
      </div>
      {promoCodes?.map(node => (
        <div className='mb-10 text-sm' key={node.id}>
          <div
            className='text-red uppercase font-semibold cursor-pointer'
            onClick={() => setParams({ promoCodesDialog: node.id }, 'replace')}
          >
            {node.name}
          </div>
        </div>
      ))}
    </div>
  )
}

export default React.memo(ProductVariantPrice)
